import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentHeader,
  DocumentSection,
  DocumentInfoGrid,
  DocumentTable,
  DocumentSignatureBlock,
  DocumentFooter,
  DocumentModalShell,
} from './DocumentBranding';
import { downloadElementAsPdf } from '../../../utils/documentPdfDownload';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const yesNo = (b) => (b === true ? 'Yes' : b === false ? 'No' : '—');

const STATUS_COLORS = {
  fully_received: 'bg-green-600',
  partially_received: 'bg-yellow-500',
  rejected: 'bg-red-600',
};

const GrnDocumentView = ({ grn, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!grn) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadElementAsPdf('procurement-document-content', `GRN_${grn.grn_number}.pdf`);
    } catch (error) {
      console.error('Error generating GRN PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const items = grn.items || [];
  const itemRows = items.map((it, i) => [
    i + 1,
    it.description || '',
    it.unit || '',
    it.qty_ordered || '',
    it.qty_received || '',
    it.qty_damaged_short || '',
    it.condition || '',
    it.remarks || '',
  ]);

  const statusLabel = (grn.overall_status || '').replace(/_/g, ' ').toUpperCase();

  return (
    <DocumentModalShell onDownload={handleDownload} onClose={onClose} downloading={downloading}>
      <DocumentHeader
        title="GOODS RECEIVED NOTE"
        subtitle={`GRN No. ${grn.grn_number}`}
        badgeLabel={statusLabel}
        badgeColor={STATUS_COLORS[grn.overall_status] || 'bg-teal-600'}
      />

      <div className="bg-blue-50 border-b border-blue-200 px-10 py-4">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">GRN Number</p>
            <p className="text-xl font-bold text-blue-900">{grn.grn_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Date Received</p>
            <p className="text-lg font-semibold text-gray-900">{fmtDate(grn.date_received)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold uppercase mb-1">PO Number</p>
            <p className="text-lg font-semibold text-gray-900">{grn.po_number || '—'}</p>
          </div>
        </div>
      </div>

      <DocumentSection title="DELIVERY INFORMATION">
        <DocumentInfoGrid
          pairs={[
            ['Supplier / Vendor Name', grn.supplier_vendor_name],
            ['Delivery Note / Invoice No.', grn.delivery_note_invoice_no],
            ['Received By', grn.received_by_name],
            ['Warehouse / Location', grn.warehouse_location],
            ['Carrier / Shipping Method', grn.carrier_shipping_method],
          ]}
        />
      </DocumentSection>

      <DocumentSection title="ITEMS RECEIVED">
        <DocumentTable
          headers={['#', 'Item Description', 'Unit', 'Qty Ord.', 'Qty Rec.', 'Damaged/Short', 'Condition', 'Remarks']}
          rows={itemRows}
        />
      </DocumentSection>

      <DocumentSection title="INSPECTION SUMMARY">
        <DocumentInfoGrid
          pairs={[
            ['Quantity Matches PO?', yesNo(grn.qty_matches_po)],
            ['Quality Acceptable?', yesNo(grn.quality_acceptable)],
            ['Packaging Intact?', yesNo(grn.packaging_intact)],
            ['Documents Attached?', yesNo(grn.documents_attached)],
            ['Overall Status', statusLabel || '—'],
            ['Follow-up Action Required', grn.follow_up_action],
          ]}
        />
        {grn.discrepancy_notes && (
          <div className="mt-4">
            <p className="text-xs text-gray-600 font-semibold uppercase mb-2">Discrepancy / Damage Notes</p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{grn.discrepancy_notes}</p>
            </div>
          </div>
        )}
        <DocumentSignatureBlock labels={['Received By', 'Inspected By', 'Warehouse Supervisor']} />
      </DocumentSection>

      <DocumentFooter documentId={grn.grn_number} />
    </DocumentModalShell>
  );
};

export default GrnDocumentView;
